import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Subtitles, pubnub, TextToSpeech, SpeechToText, MainWrapper, Select } from 'wwtc-sdk';
import {
    endpointSpeechToText,
    endpointTextToSpeech,
    endpointTranslate,
    endpointDetectLanguage,
    signedEndpoint,
} from '../constants/connection';
import specialLanguages from '../constants/specialLanguages';
import { Download, LanguageDetection, LogoutIcon, Times } from './svg/icons';
import buildSubtitlesFile from '../utils/subtitles';
import { handlePreRequestSubtitles } from './utils';
import notifications from '../notifications';

function Main() {
    // This values come from Login components
    // Also could set up constants values
    // This values are required like props of the MainWrapper
    const history = useHistory();
    const {
        userName,
        language,
        gender,
        roomName,
        apiToken,
        logoHeader,
        languagesPreTranslate,
        languages,
    } = history.location.state || {
        userName: '',
        language: '',
        gender: '',
        roomName: 'default', // default name
        apiToken: null,
        logoHeader: null,
        languagesPreTranslate: null,
        languages: [],
    };
    const myPubnubId = pubnub.userId;
    const [modalLanguageDetection, setModalLanguageDetection] = useState(false);
    const activeLanguageDetection = window.activeLanguageDetection;

    useEffect(() => {
        if (userName === '') {
            history.push(`/`, {
                userName: userName,
                language: language,
                gender: gender,
            });
        }
        pubnub.addListener({
            signal: function (s) {
                if (s.message.search('kickout') >= 0) {
                    const participantId = s.message.split('kickout_participant-')[1];
                    if (participantId === myPubnubId) {
                        pubnub.unsubscribeAll();
                        window.location.replace(window.location.origin);
                    }
                }
                if (s.message === 'clean_history_content') {
                    // clean subtitles content
                    document.getElementsByClassName('fa-trash-alt')[0].parentNode.click();
                }
                if (s.message === 'logout_admin') {
                    pubnub.unsubscribeAll();
                    window.location.replace(window.location.origin);
                }
            },
        });
    }, []);

    return (
        <MainWrapper
            autologin={false}
            gender={gender}
            language={language}
            roomName={roomName}
            userName={userName}
            languages={languages}
            notifications={notifications}
        >
            <Header
                logoHeader={logoHeader}
                pubnub={pubnub}
                roomName={roomName}
                setModalLanguageDetection={setModalLanguageDetection}
                activeLanguageDetection={activeLanguageDetection}
            />
            <WrapperComponents
                apiToken={apiToken}
                languagesPreTranslate={languagesPreTranslate}
                setModalLanguageDetection={setModalLanguageDetection}
                modalLanguageDetection={modalLanguageDetection}
                languages={languages}
            />
        </MainWrapper>
    );
}

function Header(props) {
    // initName comes from autologin component
    const { logoHeader, pubnub, initName, setModalLanguageDetection, activeLanguageDetection } =
        props;

    const handleLogout = async () => {
        if (pubnub) {
            pubnub.unsubscribeAll();
            window.location.replace(window.location.origin);
        } else {
            window.location.replace(window.location.origin);
        }
    };

    const handleKeyPress = (event, callback) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            callback();
        }
    };

    return (
        <header role="banner">
            <div className="header-left-section">
                <div className="info-user">
                    <img src={`${logoHeader}`} alt="WWTC" />
                </div>
            </div>
            <div className="header-center-section">
                <div className="direct-buttons hidden-mobile">
                    <div
                        role="heading"
                        aria-level="1"
                        tabIndex="0"
                        aria-label="Application: NOW Talk and Text - Input Interface"
                    >
                        <span>NOW Talk and Text</span>
                    </div>
                </div>
            </div>
            <div className="header-right-section">
                <div className="header-actions">
                    {activeLanguageDetection && (
                        <button
                            type="button"
                            className="lang-detection-btn"
                            data-toggle="tooltip"
                            data-placement="bottom"
                            title="Language Detection"
                            aria-label="Language Detection"
                            data-container="body"
                            onClick={() => setModalLanguageDetection(true)}
                        >
                            <LanguageDetection />
                        </button>
                    )}
                    <div className="action-group user-group">
                        <span className="profile-name hidden-mobile">{initName}</span>
                        <button
                            type="button"
                            className="logout-btn"
                            data-toggle="tooltip"
                            data-placement="bottom"
                            title="Logout"
                            aria-label="Logout"
                            aria-describedby="logout-tooltip"
                            data-container="body"
                            aria-hidden="false"
                            tabIndex="0"
                            onClick={handleLogout}
                            onKeyDown={(e) => handleKeyPress(e, handleLogout)}
                        >
                            <LogoutIcon />
                        </button>
                    </div>
                </div>
            </div>
        </header>
    );
}

function WrapperComponents(props) {
    // values from login
    const {
        apiToken,
        initName,
        initLanguage,
        initGender,
        conferenceName,
        languagesPreTranslate,
        modalLanguageDetection,
        setModalLanguageDetection,
        languages,
    } = props;
    const [isBuildingPdf, setIsBuildingPdf] = useState(false);
    const [modalDownload, setModalDownload] = useState(false);
    const [languageDetectionEnabled, setLanguageDetectionEnabled] = useState(false);
    const [valueLanguageDetection, setValueLanguageDetection] = useState(null);

    const isSpecialLanguage = (subtitles) => {
        const specialSubtitle = specialLanguages.find((special) => {
            var regex = new RegExp(special, 'g');
            var atLeastOneMatches = subtitles.some((e) => regex.test(e.speakerLanguage));
            return atLeastOneMatches;
        });

        return !!specialSubtitle;
    };

    const downloadSubtitlesPDF = (subtitles) => {
        setIsBuildingPdf(true);
        const contentPDF = buildSubtitlesFile(subtitles);

        // detect special languages
        const subtitlesWithSpecialLanguages = isSpecialLanguage(subtitles);

        // download unicode typography
        if (subtitlesWithSpecialLanguages) {
            window.print();
            setIsBuildingPdf(false);
            return;
        }

        var docDefinition = {
            content: contentPDF,
            defaultStyle: {
                font: 'Roboto',
                fontSize: 12,
            },
        };

        window.pdfMake.createPdf(docDefinition).download(() => {
            setIsBuildingPdf(false);
        });
    };

    const fetchTextToSpeechCustom = async (endpoint, text, sourceLanguage, voice, itemTtsCall) => {
        try {
            let response = await fetch(endpoint.url.replace(/:sourceLanguage/g, sourceLanguage), {
                method: endpoint.method,
                redirect: 'follow',
                body: JSON.stringify({ text }),
            });
            let data = await response.json();
            try {
                var arrayBuffer = _base64ToArrayBuffer(data.audio);
                let blob = new Blob([arrayBuffer], { type: 'audio/wav' });
                const downloadUrl = window.webkitURL.createObjectURL(blob);
                return { downloadUrl, indexCall: itemTtsCall };
            } catch (e) {
                return {
                    indexCall: itemTtsCall,
                    error: e,
                };
            }
        } catch (err) {
            return {
                indexCall: itemTtsCall,
                error: err,
            };
        }
    };

    const _base64ToArrayBuffer = (base64) => {
        var binary_string = window.atob(base64);
        var len = binary_string.length;
        var bytes = new Uint8Array(len);
        for (var i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes.buffer;
    };

    return (
        <main role="main" className="wrapper-components">
            <Subtitles.SubtitlesUI
                username={initName}
                initialSourceLanguage={initLanguage}
                initialTargetLanguage={initLanguage}
                initialVisible={true}
                defaultBilingual={false}
                endpoint={signedEndpoint(apiToken, endpointTranslate)}
                provider="pubnub"
                speechRecognition={true}
                showButtonCloseComponent={false}
                showFlipButton={true}
                showSpeedOption={true}
                showLanguageServices={true}
                style={stylesWrapper.fullContainer}
                ttsOption={true}
                channel={conferenceName}
                languagesPreTranslate={languagesPreTranslate}
                render={(subtitles) =>
                    isBuildingPdf ? null : (
                        <button
                            className="a-element"
                            onClick={() => downloadSubtitlesPDF(subtitles)}
                        >
                            <Download />
                        </button>
                    )
                }
                preRequestSubtitles={handlePreRequestSubtitles}
                requestSameLanguage={window.profanityFilterEnabled}
                bilingualTextColor={window.bilingualTextColor}
            />
            <SpeechToText.SpeechToTextUI
                username={initName}
                initialLanguage={initLanguage}
                initialVisible={false}
                initialActive={false}
                withUI={false} // to use only speech recognition
                endpoint={signedEndpoint(apiToken, endpointSpeechToText)}
                detectLanguage={languageDetectionEnabled}
                endpointDetectLanguage={signedEndpoint(apiToken, endpointDetectLanguage)}
                paramsDetectLanguage={[initLanguage, valueLanguageDetection?.value]}
            />
            <TextToSpeech.TextToSpeechUI
                initialLanguage={initLanguage}
                initialGender={initGender}
                initialVisible={false}
                initialActive={false}
                withUI={false} // to use only audio player
                endpoint={signedEndpoint(apiToken, endpointTextToSpeech)}
                fetchCustom={fetchTextToSpeechCustom}
            />
            {modalLanguageDetection ? (
                <ModalLanguageDetection
                    closeModal={() => setModalLanguageDetection(false)}
                    languages={languages}
                    languageDetectionEnabled={languageDetectionEnabled}
                    setLanguageDetectionEnabled={setLanguageDetectionEnabled}
                    valueLanguageDetection={valueLanguageDetection}
                    setValueLanguageDetection={setValueLanguageDetection}
                />
            ) : null}
            {isBuildingPdf ? <ModalDownload onCloseModal={() => setModalDownload(false)} /> : null}
        </main>
    );
}

const ModalDownload = ({ onCloseModal }) => {
    return (
        <div style={stylesWrapper.containerModal}>
            <div style={{ ...stylesWrapper.wrapperModal, height: 180, width: 450 }}>
                <div className="header-container">
                    <div className="header-title">
                        <span>PDF Subtitles</span>
                    </div>
                    <div className="header-options"></div>
                </div>
                <div
                    className="body-container"
                    style={{ ...stylesWrapper.bodyModal, textAlign: 'center' }}
                >
                    <p>Downloading PDF file...</p>
                    <p style={{ fontSize: 14 }}>Please wait, this could take a few seconds.</p>
                </div>
            </div>
        </div>
    );
};

const ModalLanguageDetection = (props) => {
    const {
        closeModal,
        languages,
        languageDetectionEnabled,
        setLanguageDetectionEnabled,
        valueLanguageDetection,
        setValueLanguageDetection,
    } = props;

    const handleOkModal = () => {
        if (!!languageDetectionEnabled && !valueLanguageDetection) {
            alert('Please, select your secondary language');
            return;
        }

        if (!languageDetectionEnabled) {
            setValueLanguageDetection(null);
            closeModal();
        }

        if (!!languageDetectionEnabled && !!valueLanguageDetection) {
            closeModal();
        }
    };

    const handleCloseModal = () => {
        if (!!languageDetectionEnabled && !valueLanguageDetection) {
            setValueLanguageDetection(null);
            setLanguageDetectionEnabled(false);
            closeModal();
            return;
        }

        if (!languageDetectionEnabled) {
            setValueLanguageDetection(null);
            closeModal();
        }

        if (!!languageDetectionEnabled && !!valueLanguageDetection) {
            closeModal();
        }
    };

    return (
        <div style={stylesWrapper.containerModal}>
            <div style={{ ...stylesWrapper.wrapperModal, height: 250 }}>
                <div className="header-container">
                    <div className="header-title">
                        <span style={{ fontSize: 14 }}>Language Detection</span>
                    </div>
                    <div className="header-options">
                        <button className="a-element" onClick={handleCloseModal}>
                            <Times />
                        </button>
                    </div>
                </div>
                <div className="body-container" style={stylesWrapper.bodyModal}>
                    <br />
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-evenly',
                            alignItems: 'center',
                        }}
                    >
                        <div>
                            <input
                                type="radio"
                                id="disabled"
                                name="languageDetectionEnabled"
                                value="disabled"
                                checked={languageDetectionEnabled === false}
                                onChange={(e) => setLanguageDetectionEnabled(false)}
                            />
                            <label htmlFor="disabled" style={{ fontSize: 14 }}>
                                Disabled
                            </label>
                        </div>

                        <div>
                            <input
                                type="radio"
                                id="enabled"
                                name="languageDetectionEnabled"
                                value="enabled"
                                checked={languageDetectionEnabled === true}
                                onChange={(e) => setLanguageDetectionEnabled(true)}
                            />
                            <label htmlFor="enabled" style={{ fontSize: 14 }}>
                                Enabled
                            </label>
                        </div>
                    </div>
                    <br />
                    {languageDetectionEnabled && (
                        <div>
                            <p style={{ fontSize: 14 }}>Select your secondary language</p>
                            <Select
                                handleChange={(lng) => setValueLanguageDetection(lng)}
                                placeholder="Select language"
                                data={languages}
                                value={valueLanguageDetection || ''}
                                showLanguageServices={true}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    )}
                </div>
                <hr />
                <div
                    className="footer-container d-flex justify-content-center align-items-center"
                    styles={stylesWrapper.footerModal}
                >
                    <button onClick={handleOkModal} className="btn btn-success btn-sm">
                        Ok
                    </button>
                </div>
            </div>
        </div>
    );
};

const stylesWrapper = {
    fullSubtitlesContainer: {
        width: 'calc(100% - 10px)',
        height: 'calc(100% - 10px)',
        margin: '5px auto',
        position: 'relative',
        zIndex: 20,
    },
    subtitlesContainer: {
        width: 'calc(100% - 10px)',
        height: 'calc(50% - 10px)',
        margin: '5px auto',
    },
    chatContainer: {
        width: 'calc(100% - 10px)',
        height: 'calc(50% - 10px)',
        margin: '5px auto',
    },
    containerModal: {
        display: 'flex',
        justifyContent: 'center',
        height: 'calc(100vh - 50px)',
        width: '100vw',
        background: 'rgba(1,1,1,0.3)',
        position: 'absolute',
        left: 0,
        top: 0,
        zIndex: 2000,
    },
    bodyModal: {
        marginTop: 24,
        height: 'calc(100% - 90px)',
        overflow: 'auto',
    },
    footerModal: {},
    wrapperModal: {
        padding: '10px 5px',
        backgroundColor: 'white',
        borderRadius: 5,
        height: 500,
        top: 'calc(50% - 250px)',
        width: 320,
        position: 'absolute',
        WebkitBoxShadow: '4px 4px 9px -1px rgba(0,0,0,0.75)',
        MozBoxShadow: '4px 4px 9px -1px rgba(0,0,0,0.75)',
        boxShadow: '4px 4px 9px -1px rgba(0,0,0,0.75)',
    },
    participantItem: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        margin: '10px 0',
        cursor: 'default',
        textIndent: '1em',
    },
    fullContainer: {
        width: 'calc(100% - 20px)',
        height: '100%',
        margin: '10px auto',
    },
};

export default Main;
